import React, { useState, useEffect } from "react";
import { db } from "../firebase.config";
import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { useAuth } from "../AuthContext";
import formatTimestamp from "../components/Timestamp";
import DateUpdate from "../components/DateUpdate";

function Gallery() {
    const [allImages, setAllImages] = useState([]);
    const [filteredImages, setFilteredImages] = useState([]);
    const [albums, setAlbums] = useState([]);
    const [currentAlbum, setCurrentAlbum] = useState("All");
    
    const [editingId, setEditingId] = useState(null);
    const [newAlbumSelection, setNewAlbumSelection] = useState("");
    const [customAlbumName, setCustomAlbumName] = useState(""); // State for the new album logic
    const [newCaption, setNewCaption] = useState("");
    
    const [isFullImgOpen, setIsFullImgOpen] = useState(false);
    const [fullImgSrc, setFullImgSrc] = useState("");
    
    const { user } = useAuth();

    const fetchData = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "photos"));
            const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const sortedData = data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setAllImages(sortedData);

            const uniqueAlbums = new Set();
            sortedData.forEach(img => {
                if (img.album && img.album.trim() !== "") {
                    uniqueAlbums.add(img.album);
                }
            });
            
            setAlbums(Array.from(uniqueAlbums).sort());
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        if (currentAlbum === "All") {
            setFilteredImages(allImages);
        } else if (currentAlbum === "General") {
            setFilteredImages(allImages.filter(img => !img.album || img.album === "General"));
        } else {
            setFilteredImages(allImages.filter(img => img.album === currentAlbum));
        }
    }, [currentAlbum, allImages]);

    const handleUpdateImage = async (imageId) => {
         const finalAlbum = customAlbumName.trim() !== "" 
            ? customAlbumName.trim() 
            : (newAlbumSelection || "General");

        try {
            await updateDoc(doc(db, "photos", imageId), {
                album: finalAlbum,
                caption: newCaption
            });
            setEditingId(null);
            setCustomAlbumName("");
            fetchData(); 
        } catch (error) {
            console.error("Error updating:", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Delete photo?")) {
            try {
                await deleteDoc(doc(db, "photos", id));
                setAllImages(prev => prev.filter(img => img.id !== id));
            } catch (error) {
                console.error("Error deleting:", error);
            }
        }
    };

    useEffect(() => { fetchData(); }, []);

    return (
        <div className="page-wrapper">
            <header className="gallery-header">
                <h1 className="gallery-title">{currentAlbum} Photos</h1>
                <div className="filter-bar">
                    <button 
                        className={`button ${currentAlbum === "All" ? "active" : ""}`}
                        onClick={() => setCurrentAlbum("All")}
                    >All</button>
                    
                    <button 
                        className={`button ${currentAlbum === "General" ? "active" : ""}`}
                        onClick={() => setCurrentAlbum("General")}
                    >General</button>

                    {albums.filter(a => a !== "General").map(name => (
                        <button 
                            key={name} 
                            className={`button ${currentAlbum === name ? "active" : ""}`}
                            onClick={() => setCurrentAlbum(name)}
                        >{name}</button>
                    ))}
                    {/* {user &&(<button 
                    onClick={DateUpdate} className="button">
                    Update Dates
                    </button>)} */}
                </div>
            </header>

            <main className="photo-grid">
                {filteredImages.length === 0 ? (
                    <div className="empty-state">No images found.</div>
                ) : (
                    filteredImages.map((image) => (
                        <div key={image.id} className="photo-card">
                            <img 
                                src={image.src || image.imageUrl} 
                                alt={image.caption} 
                                onClick={() => { setIsFullImgOpen(true); setFullImgSrc(image.src || image.imageUrl); }}
                            />
                            <div className="card-overlay">
                                <div className="overlay-content">
                                    <span className="album-tag">{image.album || "General"}</span>
                                   
                                    {editingId === image.id ? (
                                        <div className="edit-form">
                                            <input 
                                                className="caption-input"
                                                value={newCaption}
                                                onChange={(e) => setNewCaption(e.target.value)}
                                                placeholder="Caption"
                                            />
                                            <div className="edit-row">
                                                {/* Dropdown for existing metadata tags */}
                                                <select 
                                                    className="album-select"
                                                    value={newAlbumSelection}
                                                    onChange={(e) => setNewAlbumSelection(e.target.value)}
                                                >
                                                    <option value="General">General</option>
                                                    {albums.filter(a => a !== "General").map(a => (
                                                        <option key={a} value={a}>{a}</option>
                                                    ))}
                                                </select>

                                                {/* Input for NEW metadata tags */}
                                                <input 
                                                    className="album-input-small"
                                                    placeholder="Or create new..."
                                                    value={customAlbumName}
                                                    onChange={(e) => setCustomAlbumName(e.target.value)}
                                                />
                                                
                                                <button className="btn-save" onClick={() => handleUpdateImage(image.id)}>✓</button>
                                                <button className="btn-cancel" onClick={() => setEditingId(null)}>✕</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <p className="photo-caption">{image.caption || " "}</p>
                                             <p className="photo-date">{formatTimestamp(image.createdAt)}</p>
                                            {user && (
                                                <div className="admin-tools">
                                                    <button className="button" onClick={() => {
                                                        setEditingId(image.id);
                                                        setNewAlbumSelection(image.album || "General");
                                                        setNewCaption(image.caption || "");
                                                        setCustomAlbumName("");
                                                    }}>Edit</button>
                                                    <button className="button" onClick={() => handleDelete(image.id)}>Delete</button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </main>

            {isFullImgOpen && (
                <div className="full-img" onClick={() => setIsFullImgOpen(false)}>
                    <img src={fullImgSrc} alt="Full view" />
                    <span onClick={() => setIsFullImgOpen(false)}>Close</span>
                </div>
            )}
        </div>

        
    );
}

export default Gallery;